import { SITE_COMPONENTS_CLIENT_STATICS_FOLDER } from '@wix/cli-app-definitions';
import { mergeConfig } from 'vite';
import type { InlineConfig } from 'vite';
import type { ProjectModel } from '../../../model.js';
import { moduleBundleBuildSiteComponents } from '../../plugins/module-bundle/plugin.js';
import type { ViteBuildConfigOptions } from '../../vite-config-options.js';
import { draftJs } from '../../plugins/draft-js.js';
import {
  virtualSiteComponentScriptAssets,
  virtualSiteComponentPreviewScriptAssets,
} from '../../../dev-center/components/site/config.js';
import {
  preserveSiteComponentComponentsExports,
  EXPORTS_CUSTODIAN_ENTRY,
} from '../../plugins/exports-custodian.js';
import { siteComponentsExternals } from '../shared.js';
import { getBaseViteConfig } from './base.js';

export function getViteConfig(
  model: ProjectModel,
  options: ViteBuildConfigOptions
): InlineConfig {
  const virtualSiteComponentScriptEntries = [
    ...virtualSiteComponentScriptAssets(model),
  ].map(([assetId, component]) => [`script-asset-${component.id}`, assetId]);
  const virtualSiteComponentPreviewScriptEntries = [
    ...virtualSiteComponentPreviewScriptAssets(model),
  ].map(([assetId, component]) => [
    `script-asset-${component.id}-preview`,
    assetId,
  ]);

  const baseConfig = getBaseViteConfig({
    ...options,
    model,
    outputDir: SITE_COMPONENTS_CLIENT_STATICS_FOLDER,
  });

  const config = {
    build: {
      rollupOptions: {
        external: siteComponentsExternals,
        /*
         * We need to preserve names of exports from our root modules for convention.
         * "strict" or "exports-only" will not work because it will create separated files with exports.
         * It will break our naive flow of extracting css, since we trying to take css from "root" module.
         */
        preserveEntrySignatures: 'allow-extension',
        input: {
          ...Object.fromEntries([
            ...virtualSiteComponentScriptEntries,
            ...virtualSiteComponentPreviewScriptEntries,
          ]),
          [EXPORTS_CUSTODIAN_ENTRY]: EXPORTS_CUSTODIAN_ENTRY,
        },
      },
    },
    plugins: [
      moduleBundleBuildSiteComponents({ initialModel: model }),
      draftJs(),
      preserveSiteComponentComponentsExports({ initialModel: model }),
    ],
    experimental: {
      /*
        When a js chunk loads any other async js chunk,
        Vite also preloads the async chunk's dependencies.

        For example:
        in some source code:      `import('./some-other-file.js');`
        in `some-other-file.js`:  `import './some-stylesheet.css';`

        When Vite builds the above, the emitted dynamic import
        would also append a preload tag for the emitted css file

        Using `experimental.renderBuiltUrl` appends a JS expression as the baseUrl for this,
        Otherwise:
        - it would default to being relative to the page's origin
        - or we'd have to hardcode the `baseUrl` at build-time
      */
      renderBuiltUrl(filename, { hostType, hostId }) {
        if (hostType !== 'js') {
          return { relative: true };
        }

        return {
          // https://github.com/wix-private/wix-cli/pull/900#discussion_r1201541691
          runtime: `import.meta.url.replace("${hostId}", "${filename}")`,
        };
      },
    },
  } satisfies InlineConfig;

  return mergeConfig(config, baseConfig);
}
